import { ApolloClient, useApolloClient } from "@apollo/client";
import { assertNever, getDataOrNull } from "@msys/common";
import { isUndefined, reverse } from "lodash-es";
import { getUrlSearchParamsByParamsName } from "../../commons/hooks/useStateWithUrlParams.js";
import { BreadcrumbItem } from "../../commons/layout/PageBreadcrumbs.js";
import {
  PimProductPropertyInput,
  PimSearchProductsSorting,
  Scalars,
} from "../../../clients/graphqlTypes.js";
import {
  productFiltersDefaultValue,
  ProductFiltersFormValues,
} from "./filters/ProductFilterFields.js";
import {
  ItemProductSearchFilterDefaultsFragment,
  ItemProductSearchFilterDefinitionsFragment,
} from "./productSearchFilters.generated.js";
import {
  GetProductFullDescription_ProductFragment,
  GetProductFullDescription_ProductSearchResultFragment,
  usePimAddSupplierProductViewedMutation,
  useProductBreadcrumbItems__ProductCategoryTreeQuery,
} from "./helper.generated.js";
import { PimProductPropertyFragment } from "./Product.generated.js";
import {
  usePimGetSupplierCatalogue__OrganisationWriteAccessQuery,
  usePimSearchSupplierCatalogues__OrganisationWriteAccessQuery,
} from "./SupplierCatalogueAccess.generated.js";
import React, { useCallback, useEffect } from "react";
import { To, useLocation } from "react-router-dom";
import { WithProductInfoFragment } from "../doc-items/Fragments.generated.js";

/**
 * The empty sort model. PIM applies its product preference and organisation favourite boosts
 * only when a search requests no explicit `sorting`, so the default has to be empty for
 * relevance ordering to happen at all. An explicit user-chosen sort suppresses the boosts by
 * design.
 */
export const DEFAULT_PRODUCT_SORTING: PimSearchProductsSorting[] = [];

export function pimProductPropertyToInput(
  property: PimProductPropertyFragment
): PimProductPropertyInput {
  switch (property.__typename) {
    case "PimProductPropertyBool": {
      return {
        bool: {
          key: property.prop2.key,
          label: property.prop2.label,
          clientVisibility: property.prop2.clientVisibility,
          valueBool: property.prop2.valueBool,
        },
      };
    }
    case "PimProductPropertyNumber": {
      return {
        number: {
          key: property.prop2.key,
          label: property.prop2.label,
          clientVisibility: property.prop2.clientVisibility,
          valueNumber: property.prop2.valueNumber,
          unit: property.prop2.unit,
        },
      };
    }
    case "PimProductPropertyText": {
      return {
        text: {
          key: property.prop2.key,
          label: property.prop2.label,
          allowedValuesText: property.prop2.allowedValuesText.map(
            t => t.allowedText
          ),
          clientVisibility: property.prop2.clientVisibility,
          valueText: property.prop2.valueText,
        },
      };
    }
    case "PimProductPropertyRange": {
      return {
        range: {
          key: property.prop2.key,
          label: property.prop2.label,
          valueRange: property.pimProperty.valueRange,
          clientVisibility: property.prop2.clientVisibility,
          unit: property.prop2.unit,
        },
      };
    }
    case "PimProductPropertyBoolTyped": {
      return {
        boolTyped: {
          productTypeKey: property.pimProperty.productTypeKey,
          propertyTypeKey: property.pimProperty.propertyTypeKey,
          productTypeSystemKey: property.pimProperty.productTypeSystemKey,
          clientVisibility: property.prop2.clientVisibility,
          valueBool: property.prop2.valueBool,
        },
      };
    }
    case "PimProductPropertyNumberTyped": {
      return {
        numberTyped: {
          productTypeKey: property.pimProperty.productTypeKey,
          propertyTypeKey: property.pimProperty.propertyTypeKey,
          productTypeSystemKey: property.pimProperty.productTypeSystemKey,
          clientVisibility: property.prop2.clientVisibility,
          valueNumber: property.prop2.valueNumber,
          unit: property.pimProperty.unit,
        },
      };
    }
    case "PimProductPropertyTextTyped": {
      return {
        textTyped: {
          productTypeKey: property.pimProperty.productTypeKey,
          propertyTypeKey: property.pimProperty.propertyTypeKey,
          propertyTypeValueKey: property.prop2.valueText
            ? (property.pimProperty.allowedPropertyTypeValues.find(
                v => v.label === property.prop2.valueText
              )?.key ?? null)
            : null,
          productTypeSystemKey: property.pimProperty.productTypeSystemKey,
          clientVisibility: property.prop2.clientVisibility,
        },
      };
    }
    case "PimProductPropertyRangeTyped": {
      return {
        rangeTyped: {
          productTypeKey: property.pimProperty.productTypeKey,
          propertyTypeKey: property.pimProperty.propertyTypeKey,
          productTypeSystemKey: property.pimProperty.productTypeSystemKey,
          valueRange: property.pimProperty.valueRange,
          clientVisibility: property.prop2.clientVisibility,
          unit: property.pimProperty.unit,
        },
      };
    }
    default:
      throw assertNever(property);
  }
}

export function getProductFullDescription(
  product:
    | GetProductFullDescription_ProductFragment
    | GetProductFullDescription_ProductSearchResultFragment
) {
  const description = product.texts?.description?.trim();
  const extendedDescription = product.texts?.extendedDescription?.trim();
  return [
    description && !extendedDescription?.startsWith(description)
      ? description
      : null,
    extendedDescription,
  ]
    .filter(value => value)
    .join("\n");
}

/**
 * Reads a product's brand label across the two product shapes this app renders:
 * PIM *search results* expose a scalar `brand`, while resolved PIM *products*
 * (`Product`) expose it as `branding.brand.title`.
 */
export function getProductBrand(
  product:
    | { brand?: string | null }
    | { branding?: { brand?: { title?: string | null } | null } | null }
): string | null {
  if ("brand" in product) return product.brand ?? null;
  if ("branding" in product) return product.branding?.brand?.title ?? null;
  return null;
}

/**
 * The authored text values a brand-style filter carries, or null when it has
 * none to give — it is expression-driven, or absent. Only the authored flavour
 * has a value here; a computed filter's value reaches the search through the
 * resolved defaults instead.
 */
function textFilterValues(
  filter:
    | ItemProductSearchFilterDefinitionsFragment["brandFilter"]
    | ItemProductSearchFilterDefaultsFragment["brandFilter"]
): string[] | null {
  if (!filter) return null;
  if (filter.__typename === "EntitySearchTextFilter") {
    return filter.valueText !== null && filter.valueText !== undefined
      ? [filter.valueText]
      : null;
  }
  if (filter.__typename === "EntitySearchTextInFilter") {
    return filter.valueTextIn ?? null;
  }
  return null;
}

/** One bound of an authored list-price filter, or null when it has none. */
export function priceBound(
  filter:
    | ItemProductSearchFilterDefinitionsFragment["listPriceFilter"]
    | ItemProductSearchFilterDefaultsFragment["listPriceFilter"],
  bound: "min" | "max"
): number | null {
  if (!filter) return null;
  if (filter.__typename === "EntitySearchNumberFilter") {
    const inclusive =
      bound === "max"
        ? ["lt", "lte", "eq"].includes(filter.operatorNumber)
        : ["gt", "gte", "eq"].includes(filter.operatorNumber);
    return inclusive ? (filter.valueNumber ?? null) : null;
  }
  if (filter.__typename === "EntitySearchNumberBetweenFilter") {
    return filter.valueNumberBetween?.[bound] ?? null;
  }
  return null;
}

import { productSearchFilterPropertyFiltersToFormValues } from "../doc-items/productSearchFilterSlots.js";

export function definitionsToFormValues(
  definitions:
    | ItemProductSearchFilterDefaultsFragment
    | ItemProductSearchFilterDefinitionsFragment,
  item?: WithProductInfoFragment & {
    pendingChangeAttributes: Scalars["JSON"]["output"];
    productPlaceholder?: { productTypeKey: string } | null;
  },
  /** The key of the type of `item.product`, for an item without a placeholder. */
  productTypeKeyOfProduct?: string | null
): ProductFiltersFormValues {
  // taking additional filters from product set in item
  const productBrand: string | undefined = !isUndefined(
    item?.pendingChangeAttributes["productBrand"]
  )
    ? item?.pendingChangeAttributes["productBrand"]
    : item?.product?.brand;
  const productBrandLine: string | undefined = !isUndefined(
    item?.pendingChangeAttributes["productBrandLine"]
  )
    ? item?.pendingChangeAttributes["productBrandLine"]
    : item?.product?.brandLine;
  return {
    organisationFavourite:
      definitions.favouritesFilter?.__typename === "EntitySearchBoolFilter"
        ? (definitions.favouritesFilter.valueBool ?? null)
        : null,
    productTypeKeys: item?.productPlaceholder
      ? [item.productPlaceholder.productTypeKey]
      : productTypeKeyOfProduct
        ? [productTypeKeyOfProduct]
        : null,
    supplierIds: definitions.supplierIdFilter
      ? definitions.supplierIdFilter.__typename === "EntitySearchIdFilter"
        ? [definitions.supplierIdFilter.valueId]
        : definitions.supplierIdFilter.valueIdIn
      : null,
    brands:
      textFilterValues(definitions.brandFilter) ??
      (productBrand ? [productBrand] : null),
    brandLines:
      textFilterValues(definitions.brandLineFilter) ??
      (productBrandLine ? [productBrandLine] : null),
    supplierProductStatuses: null,
    listPriceMax: priceBound(definitions.listPriceFilter, "max"),
    listPriceMin: priceBound(definitions.listPriceFilter, "min"),

    properties: productSearchFilterPropertyFiltersToFormValues(
      definitions.propertyFilters
    ),

    supplierProductCategoryKey: null,
    supplierCatalogues: null,
  };
}

export function useCanCreateSupplierCatalogueProduct() {
  const client = useApolloClient();
  const query = usePimSearchSupplierCatalogues__OrganisationWriteAccessQuery({
    client,
    fetchPolicy: "cache-and-network",
  });

  const supplierCatalogues = getDataOrNull(
    query.data?.pimSearchSupplierCatalogues
  )?.supplierCatalogues;

  return {
    canCreateSupplierCatalogueProduct: Boolean(
      supplierCatalogues && supplierCatalogues.length > 0
    ),
    loading: query.loading,
  };
}

export function useCanEditSupplierCatalogueProduct(
  supplierId: string,
  supplierCatalogueId: string
) {
  const client = useApolloClient();
  const query = usePimGetSupplierCatalogue__OrganisationWriteAccessQuery({
    client,
    variables: { supplierId, supplierCatalogueId },
    fetchPolicy: "cache-and-network",
  });

  const supplierCatalogue = getDataOrNull(
    query.data?.pimGetSupplierCatalogue
  )?.supplierCatalogue;

  return {
    canEditSupplierCatalogueProduct:
      supplierCatalogue &&
      supplierCatalogue.supplierCatalogueAccess.organisationCanWrite,
    loading: query.loading,
  };
}

export function useProductViewed({ client }: { client: ApolloClient<object> }) {
  const [pimAddSupplierProductViewedMutation] =
    usePimAddSupplierProductViewedMutation({ client });

  const addProductViewed = useCallback(
    async (product: { articleNumber: string; supplierId: string }) => {
      await pimAddSupplierProductViewedMutation({
        variables: {
          input: {
            articleNumber: product.articleNumber,
            supplierId: product.supplierId,
          },
        },
      });
    },
    [pimAddSupplierProductViewedMutation]
  );

  return {
    addProductViewed,
  };
}

export function useProductViewedEffect({
  client,
  product: { articleNumber, supplierId },
}: {
  client: ApolloClient<object>;
  product: { articleNumber: string; supplierId: string };
}) {
  const { addProductViewed } = useProductViewed({ client });

  useEffect(() => {
    addProductViewed({ articleNumber, supplierId });
  }, [articleNumber, supplierId, addProductViewed]);
}

export function useProductBreadcrumbs(
  supplierId: string | null,
  categoryKey: string | null,
  productTitle: string
) {
  const client = useApolloClient();

  const query = useProductBreadcrumbItems__ProductCategoryTreeQuery({
    client,
    variables: { supplierId: supplierId!, categoryKey: categoryKey! },
    skip: !supplierId || !categoryKey,
  });

  const breadcrumbItems = React.useMemo((): BreadcrumbItem[] => {
    const productParentCategories =
      getDataOrNull(
        (query.data ?? query.previousData)?.pimGetSupplierProductCategoryTree
      )?.tree?.parents ?? [];
    const productCategory = getDataOrNull(
      (query.data ?? query.previousData)?.pimGetSupplierProductCategoryTree
    )?.tree?.productCategory;

    const items: BreadcrumbItem[] = (
      productCategory
        ? [...reverse([...productParentCategories]), productCategory]
        : []
    ).map(category => ({
      title: category.title,
      to: {
        pathname: `/products/catalogues`,
        search: getUrlSearchParamsByParamsName(
          {
            ...productFiltersDefaultValue,
            supplierProductCategoryKey: category.key,
            supplierIds: [supplierId],
          },
          "filters"
        ).toString(),
      },
    }));
    if (items.length > 0) {
      items.push({ title: productTitle, to: "/" });
    }
    return items;
  }, [supplierId, productTitle, query.data, query.previousData]);

  return breadcrumbItems;
}

/**
 * Where a product row leads: the product's overview, with the current location as the return
 * link. Shared by every list that renders product search results.
 */
export function useGetProductTo() {
  const location = useLocation();

  return useCallback(
    (item: { supplierId: string; articleNumber: string }): To => ({
      pathname: `/products/catalogues/supplier/${
        item.supplierId
      }/${encodeURIComponent(item.articleNumber)}/overview`,
      search: new URLSearchParams({
        r: location.pathname + location.search,
      }).toString(),
    }),
    [location]
  );
}
